import { useSelector } from 'react-redux';
import { useParams } from 'react-router-dom';
import { Box, FormControl, FormLabel, Stack } from '@chakra-ui/react';

import useGetEngine from './queries/useGetEngine';

import FormBase from '@/components/forms/new/FormBase';
import { LoadingIndicator } from 'components/v4';
import NameField from 'components/forms/new/formikFormFields/NameField';
import {
    formLabelStyling,
    stackSpacing,
    stackStyling,
} from 'components/forms/new/formStyles';
import {
    breadCrumbTrailView as breadCrumbTrail,
    viewHeader as header,
    enginesPath,
} from './constants';

/**
 * Read-only field used on the Engine view screen, built from the same
 * NameField used by genuinely read-only fields elsewhere (e.g. company
 * InitialDomain) so height/spacing match the rest of the app exactly. There
 * is no update capability for engines today, so this is never editable.
 */
const ReadOnlyField = ({ label, value, darkMode }) => (
    <FormControl sx={{ margin: '0 !important' }}>
        <Stack direction="row" spacing={stackSpacing} sx={stackStyling()}>
            <FormLabel sx={formLabelStyling(darkMode)}>{label}</FormLabel>
            <Box flex={1} maxWidth="400px">
                <NameField
                    field={{ name: label, value: value || '' }}
                    darkMode={darkMode}
                    isReadOnly
                />
            </Box>
        </Stack>
    </FormControl>
);

const EnginesView = () => {
    const { id } = useParams();
    const { darkMode } = useSelector((state) => state.settings);

    const {
        data: engineData,
        isLoading: engineLoading,
        isFetching: engineFetching,
        isError: notFound,
    } = useGetEngine(id);

    if (engineLoading || engineFetching) {
        return <LoadingIndicator />;
    }

    if (notFound) {
        return (
            <FormBase
                breadCrumbTrail={breadCrumbTrail}
                header={header}
                initialValues={{}}
                finalFormSubmit={async () => {}}
                hideSubmitButton
                backLink={[enginesPath, { state: { index: 0 } }]}>
                {() => [
                    <p key="not-found">
                        This engine no longer exists. It may have already been
                        deleted.
                    </p>,
                ]}
            </FormBase>
        );
    }

    return (
        <FormBase
            breadCrumbTrail={breadCrumbTrail}
            header={header}
            initialValues={{}}
            finalFormSubmit={async () => {}}
            hideSubmitButton
            backLink={[enginesPath, { state: { index: 0 } }]}>
            {() => [
                <ReadOnlyField
                    key="name"
                    label="Name"
                    value={engineData?.name}
                    darkMode={darkMode}
                />,
                <ReadOnlyField
                    key="id"
                    label="ID"
                    value={engineData?.id}
                    darkMode={darkMode}
                />,
                <ReadOnlyField
                    key="accessKey"
                    label="Access Key"
                    value={engineData?.accessKey}
                    darkMode={darkMode}
                />,
            ]}
        </FormBase>
    );
};

export default EnginesView;
